import { BusinessService } from "../types/businessService";
import { Product } from "../types/product";

export interface PosCartServiceItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  taxPercent: number;
  durationLabel: string;
}

export interface PosCartDealItem {
  id: number;
  name: string;
  price: number;
  originalPrice: number;
  quantity: number;
  itemsCount: number;
}

export interface PosTax {
  id: number;
  percent: number;
}

export function getServiceNetPrice(service: BusinessService): number {
  const { price, discountPrice } = service;
  if (discountPrice !== undefined && discountPrice < price) {
    return discountPrice;
  }
  return price;
}

export function getServiceTaxPercent(
  service: BusinessService,
  allTaxes: PosTax[],
): number {
  const raw = service.raw as Record<string, unknown> | undefined;
  const embeddedTaxes =
    (raw?.service_taxes as Array<{ tax?: { percent?: number } }>) ||
    (raw?.tax_services as Array<{ tax?: { percent?: number } }>);

  if (Array.isArray(embeddedTaxes) && embeddedTaxes.length > 0) {
    return embeddedTaxes.reduce(
      (sum, item) => sum + (item.tax?.percent || 0),
      0,
    );
  }

  const taxIds: number[] =
    (raw?.service_taxes as Array<{ tax_id?: number; id?: number }>)?.map((t) =>
      Number(t.tax_id || t.id),
    ) ||
    (raw?.tax_services as Array<{ tax_id?: number }>)?.map((t) =>
      Number(t.tax_id),
    ) ||
    [];

  if (taxIds.length > 0 && allTaxes.length > 0) {
    return allTaxes
      .filter((t) => taxIds.includes(Number(t.id)))
      .reduce((sum, t) => sum + (t.percent || 0), 0);
  }

  return 0;
}

export function calculatePosCartTotals(
  cartProducts: Product[],
  cartServices: PosCartServiceItem[],
  cartDeals: PosCartDealItem[] = [],
) {
  let originalNet = 0;
  let discount = 0;
  let net = 0;
  let tax = 0;

  cartProducts.forEach((p) => {
    const totalTaxPercent =
      p.taxes?.reduce((sum, t) => sum + t.percent, 0) || 0;
    const originalPrice = p.price;
    const baseNet =
      p.discountedPrice !== undefined &&
      p.discountedPrice !== null &&
      p.discountedPrice < p.price
        ? p.discountedPrice
        : p.price;
    const qty = p.quantity ?? 1;
    originalNet += originalPrice * qty;
    discount += (originalPrice - baseNet) * qty;
    net += baseNet * qty;
    tax += baseNet * qty * (totalTaxPercent / 100);
  });

  cartServices.forEach((s) => {
    const qty = s.quantity ?? 1;
    const lineNet = s.price * qty;
    originalNet += lineNet;
    net += lineNet;
    tax += lineNet * (s.taxPercent / 100);
  });

  cartDeals.forEach((d) => {
    const qty = d.quantity ?? 1;
    originalNet += d.originalPrice * qty;
    discount += Math.max(0, d.originalPrice - d.price) * qty;
    net += d.price * qty;
  });

  return { originalNet, discount, net, tax, total: net + tax };
}
